import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import AppLayout from './layout/AppLayout';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import VerifyPage from './pages/VerifyPage';
import HomePage from './pages/HomePage';
import UsersPage from './pages/UsersPage';
import UserFormPage from './pages/UserFormPage';
import ProductsPage from './pages/ProductsPage';
import ProductFormPage from './pages/ProductFormPage';
import MediasPage from './pages/MediasPage';
import ProfilePage from './pages/ProfilePage';
import CustomersPage from './pages/CustomersPage';
import CustomerFormPage from './pages/CustomerFormPage';
import OrdersPage from './pages/OrdersPage';
import OrderFormPage from './pages/OrderFormPage';
import OrderPaymentReceiptPage from './pages/OrderPaymentReceiptPage';
import OrderPublicFormPage from './pages/OrderPublicFormPage';
import OrderQuickIntakePage from './pages/OrderQuickIntakePage';
import SettingsPage from './pages/SettingsPage';
import OrderWorkflowSettingsPage from './pages/OrderWorkflowSettingsPage';
import WorkflowEditorPage from './pages/WorkflowEditorPage';
import CreateWorkspacePage from './pages/CreateWorkspacePage';
import AdminBackupPage from './pages/AdminBackupPage';
import ExtensionsPage from './extensions/ExtensionsPage';
import { EXTENSION_CATALOG } from './extensions/extensionRegistry';
import { isAuthenticated } from './app/auth';
import { LanguageProvider, useTranslation } from './app/LanguageContext';
import { WorkspaceProvider } from './app/WorkspaceContext';
import { ThemeModeProvider } from './app/ThemeModeContext';
import { ExtensionsProvider, useExtensions } from './app/ExtensionsContext';
import { Box, CircularProgress, Alert, Button } from '@mui/material';

const LoginRoute = () => {
  if (isAuthenticated()) {
    return <Navigate to="/" replace />;
  }
  return <LoginPage />;
};

const ExtensionRoute = ({ extensionId, children }) => {
  const { isEnabled, loading, loadError, reload } = useExtensions();
  const { translate } = useTranslation();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress aria-label={translate('common.loading')} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          px: 2,
        }}
      >
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => reload()}>
              {translate('auth.session_retry')}
            </Button>
          }
          sx={{ maxWidth: 480 }}
        >
          {translate('extensions.load_error')}
        </Alert>
      </Box>
    );
  }

  if (!isEnabled(extensionId)) return <Navigate to="/" replace />;
  return children;
};

const LegacyFormularioRedirect = () => {
  const { workspaceId, token } = useParams();
  return <Navigate to={`/form/${workspaceId}/${token}`} replace />;
};

const ProductIdToEditRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/products/${encodeURIComponent(id)}/edit`} replace />;
};

const OrderIdToEditRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/orders/${encodeURIComponent(id)}/edit`} replace />;
};

function App() {
  return (
    <ThemeModeProvider>
      <LanguageProvider>
      <WorkspaceProvider>
      <ExtensionsProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/verify" element={<VerifyPage />} />
          <Route path="/form/:workspaceId/:token" element={<OrderPublicFormPage />} />
          <Route path="/solicitacao/:workspaceId" element={<OrderQuickIntakePage />} />
          <Route path="/formulario/:workspaceId/:token" element={<LegacyFormularioRedirect />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <HomePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <UsersPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/new"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <UserFormPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/:id/edit"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <UserFormPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ProductsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/new"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ProductFormPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/:id/edit"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ProductFormPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/:id"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ProductIdToEditRedirect />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/medias"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <MediasPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <CustomersPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers/new"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <CustomerFormPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers/:id/edit"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <CustomerFormPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <OrdersPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/new"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <OrderFormPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/:orderId/payments/:paymentId/receipt"
            element={
              <ProtectedRoute>
                <OrderPaymentReceiptPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/:id"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <OrderIdToEditRedirect />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/:id/edit"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <OrderFormPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <SettingsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/order-workflow/types/:typeId/edit"
            element={
              <ProtectedRoute>
                <WorkflowEditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/order-workflow"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Navigate to="/settings/order-workflow/catalog" replace />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/order-workflow/:section"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <OrderWorkflowSettingsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ProfilePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/workspaces/new"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <CreateWorkspacePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/backup"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <AdminBackupPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          {EXTENSION_CATALOG.map((ext) => {
            const Page = ext.Page;
            return (
              <Route
                key={ext.id}
                path={ext.route}
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <ExtensionRoute extensionId={ext.id}>
                        <Page />
                      </ExtensionRoute>
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
            );
          })}
          <Route
            path="/extensions"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ExtensionsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/workspace/:workspaceId/settings"
            element={<Navigate to="/settings" replace />}
          />
          <Route
            path="*"
            element={
              isAuthenticated() ? (
                <Navigate to="/" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </Router>
      </ExtensionsProvider>
      </WorkspaceProvider>
      </LanguageProvider>
    </ThemeModeProvider>
  );
}

export default App;

